import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@arabquiz/ui'
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { ConfirmDialog } from '../question-bank/ConfirmDialog'
import { QuestionBankItemForm } from '../question-bank/QuestionBankItemForm'
import type { BankItem } from '../question-bank/types'
import {
  AdminEmptyState,
  AdminErrorBanner,
  AdminPagination,
  AdminSearchBar,
} from './AdminTableChrome'
import {
  ADMIN_PAGE_SIZE,
  adminKeys,
  formatDateAr,
  type AdminBankCategory,
  type AdminBankItem,
  type AdminBankListResponse,
  type AdminBankType,
} from './types'

const TYPE_OPTIONS: AdminBankType[] = ['MultipleChoice', 'TrueFalse', 'OrderSequence']
const SCOPE_OPTIONS = ['all', 'public', 'private'] as const

/** Map an admin bank item to the form's `BankItem` shape (admin items have no `isOwnedByMe`). */
function toBankItem(item: AdminBankItem): BankItem {
  return {
    id: item.id,
    type: item.type,
    text: item.text,
    timeSeconds: item.timeSeconds,
    difficulty: item.difficulty,
    categoryId: item.categoryId,
    categoryName: item.categoryName,
    scope: item.scope,
    isOwnedByMe: false,
    options: item.options,
    correctOptionIndex: item.correctOptionIndex,
    correctAnswer: item.correctAnswer,
    items: item.items,
    explanation: item.explanation,
    updatedAt: item.updatedAt,
  }
}

/**
 * Admin cross-org Question Bank table. Lists every item (public + every org's
 * private) from `GET /admin/question-bank/items` with search, category, scope
 * and type filters, and supports create/edit/delete on ANY item via the admin
 * endpoints. Matches the user/quiz table conventions (semantic table from
 * `md`, stacked cards below).
 */
export function AdminBankTable() {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const { t: tQuiz } = useTranslation('quiz')
  const queryClient = useQueryClient()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [scope, setScope] = useState<(typeof SCOPE_OPTIONS)[number]>('all')
  const [type, setType] = useState<'' | AdminBankType>('')
  const [page, setPage] = useState(1)
  const [formItem, setFormItem] = useState<BankItem | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<AdminBankItem | null>(null)

  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [searchInput])

  const filters = { search, categoryId: categoryId || null, scope, type, page }

  const itemsQuery = useQuery({
    queryKey: adminKeys.bank(filters),
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (categoryId) params.set('categoryId', categoryId)
      if (scope !== 'all') params.set('scope', scope)
      if (type) params.set('type', type)
      params.set('page', String(page))
      params.set('pageSize', String(ADMIN_PAGE_SIZE))
      const res = await fetch(`/api/v1/admin/question-bank/items?${params.toString()}`, {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load bank items: ${res.status}`)
      }
      return (await res.json()) as AdminBankListResponse
    },
  })

  const categoriesQuery = useQuery({
    queryKey: ['questionBank', 'categories'],
    queryFn: async ({ signal }) => {
      const res = await fetch('/api/v1/question-bank/categories', {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load categories: ${res.status}`)
      }
      return (await res.json()) as AdminBankCategory[]
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (item: AdminBankItem) => {
      const res = await fetch(
        `/api/v1/admin/question-bank/items/${encodeURIComponent(item.id)}`,
        { method: 'DELETE', credentials: 'include' },
      )
      if (!res.ok) {
        throw new Error(`Failed to delete item: ${res.status}`)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.bankRoot })
      void queryClient.invalidateQueries({ queryKey: adminKeys.analytics })
      setPendingDelete(null)
    },
  })

  function typeLabel(value: AdminBankType): string {
    return value === 'MultipleChoice'
      ? t('typeMcq')
      : value === 'TrueFalse'
        ? t('typeTf')
        : t('typeOrder')
  }

  function difficultyLabel(value: AdminBankItem['difficulty']): string {
    if (value === 'Easy') return t('difficultyEasy')
    if (value === 'Medium') return t('difficultyMedium')
    if (value === 'Hard') return t('difficultyHard')
    return '—'
  }

  function openCreate() {
    setFormItem(null)
    setFormOpen(true)
  }

  function openEdit(item: AdminBankItem) {
    setFormItem(toBankItem(item))
    setFormOpen(true)
  }

  const total = itemsQuery.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / ADMIN_PAGE_SIZE))
  const items = itemsQuery.data?.items ?? []
  const hasFilters = search.length > 0 || categoryId !== '' || scope !== 'all' || type !== ''

  return (
    <div>
      {/* Controls. */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <AdminSearchBar value={searchInput} onChange={setSearchInput} placeholder={t('searchBank')} />
          <Button
            type="button"
            onClick={openCreate}
            className="gap-2 bg-gradient-to-bl from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 shadow-md hover:shadow-lg"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            <span>{tQuiz('bankCreateItem')}</span>
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label={t('bankColCategory')}
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value)
              setPage(1)
            }}
            className="h-10 rounded-md border border-brand-200 bg-white ps-3 pe-8 text-sm text-brand-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <option value="">{t('bankAllCategories')}</option>
            {(categoriesQuery.data ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          <select
            aria-label={t('bankColScope')}
            value={scope}
            onChange={(e) => {
              setScope(e.target.value as (typeof SCOPE_OPTIONS)[number])
              setPage(1)
            }}
            className="h-10 rounded-md border border-brand-200 bg-white ps-3 pe-8 text-sm text-brand-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <option value="all">{t('bankScopeAll')}</option>
            <option value="public">{t('bankScopePublic')}</option>
            <option value="private">{t('bankScopePrivate')}</option>
          </select>

          <select
            aria-label={t('bankColType')}
            value={type}
            onChange={(e) => {
              setType(e.target.value as '' | AdminBankType)
              setPage(1)
            }}
            className="h-10 rounded-md border border-brand-200 bg-white ps-3 pe-8 text-sm text-brand-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <option value="">{t('bankAllTypes')}</option>
            {TYPE_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {typeLabel(value)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6">
        {itemsQuery.isLoading ? <TableSkeleton /> : null}

        {itemsQuery.isError ? <AdminErrorBanner message={t('loadError')} /> : null}

        {itemsQuery.isSuccess && items.length === 0 ? (
          <AdminEmptyState message={hasFilters ? t('noResults') : t('noBankItems')} />
        ) : null}

        {itemsQuery.isSuccess && items.length > 0 ? (
          <>
            {/* Desktop: semantic table. */}
            <div className="hidden overflow-hidden rounded-2xl border border-brand-100 bg-white md:block">
              <table className="w-full border-collapse text-sm" dir="rtl">
                <thead>
                  <tr className="border-b border-brand-100 bg-brand-50/40 text-xs font-semibold uppercase tracking-wide text-brand-600">
                    <th scope="col" className="px-4 py-3 text-start">{t('bankColText')}</th>
                    <th scope="col" className="px-4 py-3 text-start">{t('bankColType')}</th>
                    <th scope="col" className="px-4 py-3 text-start">{t('bankColCategory')}</th>
                    <th scope="col" className="px-4 py-3 text-start">{t('bankColScope')}</th>
                    <th scope="col" className="px-4 py-3 text-start">{t('bankColDifficulty')}</th>
                    <th scope="col" className="px-4 py-3 text-start">{t('bankColUpdated')}</th>
                    <th scope="col" className="px-4 py-3 text-end">{t('bankColActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b border-brand-50 last:border-0 hover:bg-brand-50/30">
                      <td className="px-4 py-3 align-top max-w-sm">
                        <div className="truncate font-medium text-brand-900" title={item.text}>
                          {item.text}
                        </div>
                        {item.explanation ? (
                          <div className="mt-1 truncate text-xs text-brand-500" title={item.explanation}>
                            {tQuiz('explanation')}: {item.explanation}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 align-top text-brand-700">{typeLabel(item.type)}</td>
                      <td className="px-4 py-3 align-top text-brand-700">
                        {item.categoryName || t('bankUncategorized')}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <ScopeBadge
                          scope={item.scope}
                          publicLabel={t('bankScopePublic')}
                          privateLabel={t('bankScopePrivate')}
                        />
                      </td>
                      <td className="px-4 py-3 align-top text-brand-700">{difficultyLabel(item.difficulty)}</td>
                      <td className="px-4 py-3 align-top text-brand-700">
                        <span dir="ltr">{formatDateAr(item.updatedAt)}</span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <ItemActions
                          busy={deleteMutation.isPending && pendingDelete?.id === item.id}
                          editLabel={tCommon('edit')}
                          deleteLabel={tCommon('delete')}
                          onEdit={() => openEdit(item)}
                          onDelete={() => setPendingDelete(item)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: stacked cards. */}
            <ul role="list" className="space-y-4 md:hidden">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-500 ps-4 pe-4 py-4 shadow-sm"
                >
                  <div className="font-medium text-brand-900" title={item.text}>
                    {item.text}
                  </div>
                  {item.explanation ? (
                    <div className="mt-1 text-xs text-brand-500">
                      {tQuiz('explanation')}: {item.explanation}
                    </div>
                  ) : null}
                  <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <dt className="text-brand-500">{t('bankColType')}</dt>
                    <dd className="text-brand-800 text-end">{typeLabel(item.type)}</dd>
                    <dt className="text-brand-500">{t('bankColCategory')}</dt>
                    <dd className="text-brand-800 text-end">{item.categoryName || t('bankUncategorized')}</dd>
                    <dt className="text-brand-500">{t('bankColScope')}</dt>
                    <dd className="text-end">
                      <ScopeBadge
                        scope={item.scope}
                        publicLabel={t('bankScopePublic')}
                        privateLabel={t('bankScopePrivate')}
                      />
                    </dd>
                    <dt className="text-brand-500">{t('bankColDifficulty')}</dt>
                    <dd className="text-brand-800 text-end">{difficultyLabel(item.difficulty)}</dd>
                  </dl>
                  <div className="mt-3 flex justify-end">
                    <ItemActions
                      busy={deleteMutation.isPending && pendingDelete?.id === item.id}
                      editLabel={tCommon('edit')}
                      deleteLabel={tCommon('delete')}
                      onEdit={() => openEdit(item)}
                      onDelete={() => setPendingDelete(item)}
                    />
                  </div>
                </li>
              ))}
            </ul>

            <AdminPagination
              page={page}
              pageCount={pageCount}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(pageCount, p + 1))}
            />
          </>
        ) : null}

        {itemsQuery.isLoading ? (
          <span className="sr-only" role="status" aria-live="polite">
            {tCommon('loading')}
          </span>
        ) : null}
      </div>

      <QuestionBankItemForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        item={formItem}
        adminMode
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('bankDeleteTitle')}
        message={t('bankDeleteConfirm')}
        confirmPending={deleteMutation.isPending}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete)
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  )
}

type ItemActionsProps = {
  busy: boolean
  editLabel: string
  deleteLabel: string
  onEdit: () => void
  onDelete: () => void
}

function ItemActions({ busy, editLabel, deleteLabel, onEdit, onDelete }: ItemActionsProps) {
  return (
    <div className="flex items-center justify-end gap-1.5">
      <button
        type="button"
        onClick={onEdit}
        aria-label={editLabel}
        title={editLabel}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-brand-200 text-brand-700 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <Pencil className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={busy}
        aria-label={deleteLabel}
        title={deleteLabel}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Trash2 className="h-4 w-4" aria-hidden="true" />}
      </button>
    </div>
  )
}

function ScopeBadge({
  scope,
  publicLabel,
  privateLabel,
}: {
  scope: 'Public' | 'Private'
  publicLabel: string
  privateLabel: string
}) {
  return scope === 'Public' ? (
    <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-semibold text-brand-700">
      {publicLabel}
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-ink-50 px-2.5 py-0.5 text-xs font-semibold text-brand-600">
      {privateLabel}
    </span>
  )
}

function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-brand-100 bg-white" aria-hidden="true">
      <div className="space-y-px">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-4 animate-pulse">
            <div className="h-6 flex-1 rounded bg-brand-50" />
            <div className="h-6 w-20 rounded bg-brand-50" />
            <div className="h-6 w-16 rounded bg-brand-50" />
          </div>
        ))}
      </div>
    </div>
  )
}
